import React from 'react';
import {
	type FieldValues,
	type FieldPath,
	type PathValue,
} from 'react-hook-form';
import {splitEventHandler} from '@netx/core-next/utils';
import {useConstCallback} from '@netx/core-components/hooks/useEvent';
import {combineRefs} from '@netx/core-components/util';
import {
	SwitchListItem,
	type SwitchListItemProps,
} from '@netx/core-components/SwitchListItem';
import {
	ReactHookFormControl,
	type WithReactHookFormControlFieldProps,
} from '../ReactHookFormControl';

export interface ReactHookFormSwitchListItemProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends WithReactHookFormControlFieldProps<
		Omit<SwitchListItemProps, 'checked'>,
		TFieldValues,
		TName
	> {
	/** hook for parsing the value from the form to the input */
	parseValueIn?: (value: PathValue<TFieldValues, TName>) => boolean;
	/** hook for parsing the value from the input to the form */
	parseValueOut?: (value: boolean) => PathValue<TFieldValues, TName>;
}

export const ReactHookFormSwitchListItemPrimitive = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	{
		control,
		parseValueIn,
		parseValueOut,
		ControllerProps,
		...props
	}: ReactHookFormSwitchListItemProps<TFieldValues, TName>,
	ref: any,
) => {
	return (
		<ReactHookFormControl<TFieldValues, TName>
			{...ControllerProps}
			control={control}
			name={props.name as TName}
			render={({field, formState}) => (
				<SwitchListItem
					// error={formState.errors[props.name]?.message?.toString()}
					{...props}
					{...field}
					// value={parseValueIn ? parseValueIn(field.value) : field.value}
					checked={parseValueIn ? parseValueIn(field.value) : field.value}
					disabled={props.disabled || field.disabled}
					ref={ref}
					inputRef={combineRefs(props.inputRef, field.ref)}
					onChange={useConstCallback(
						splitEventHandler(props.onChange, (event, checked) => {
							field.onChange(parseValueOut ? parseValueOut(checked) : checked);
						}),
					)}
				/>
			)}
			rules={{
				required: props.required,
				...ControllerProps?.rules,
			}}
		/>
	);
};

/**
 * `react-hook-form` controlled `SwitchListItem` component
 */
export const ReactHookFormSwitchListItem = React.forwardRef(
	ReactHookFormSwitchListItemPrimitive,
) as <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	props: ReactHookFormSwitchListItemProps<TFieldValues, TName>,
) => JSX.Element;

export default ReactHookFormSwitchListItem;
